import { useQuery } from '@tanstack/react-query';
import { BaselineStackStatus, type AccountBaselineView } from '../../../types/backend';
import { useAwsAccountService } from '../services/awsAccountService';
import { awsAccountKeys } from './queryKeys';

/** How often to re-read while an account is still converging, or still empty. */
const CONVERGING_POLL_MS = 15_000;

/**
 * Per-account onboarding status for one customer.
 *
 * Polls only while something is unsettled. An account whose stacks have all
 * converged, or that is blocked waiting on a person, is not going to change on
 * its own, so a steady-state view costs one read.
 */
export function useAccountBaselineStatus(customerId: string, enabled = true) {
  const service = useAwsAccountService();

  const query = useQuery({
    queryKey: awsAccountKeys.baselineStatus(customerId),
    queryFn: () => service.getAccountBaselineStatus(customerId),
    enabled: enabled && Boolean(customerId),
    refetchInterval: (q) =>
      q.state.data && isWatchworthy(q.state.data.accounts) ? CONVERGING_POLL_MS : false,
  });

  return {
    accounts: query.data?.accounts ?? [],
    loading: query.isFetching,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load onboarding status'
      : null,
    refresh: query.refetch,
  };
}

/**
 * Whether anything here could change without a person acting.
 *
 * An account with no stacks counts. That is the state of a fresh account, and
 * it is precisely when someone is watching for the first stack to appear:
 * treating "nothing unsettled" as "nothing to wait for" would leave the panel
 * frozen through the one moment it exists to show. A stack blocked or refused
 * does not count, because it waits on a person and nothing will move until one
 * acts. The query only runs while the panel is mounted, so this polls for as
 * long as someone is looking and stops when they close it.
 */
export function isWatchworthy(accounts: AccountBaselineView[]): boolean {
  return accounts.some((account) => {
    const stacks = account.stacks ?? [];
    if (stacks.length === 0) return true;
    return stacks.some(
      (s) =>
        s.status === BaselineStackStatus.APPLYING ||
        s.status === BaselineStackStatus.PENDING ||
        s.status === BaselineStackStatus.NOT_STARTED,
    );
  });
}
